import { type Configuration, LogLevel } from '@azure/msal-browser';
import { environment } from '@environments/environment';

/**
 * MSAL (Microsoft Entra ID) configuration for the LabFlow SPA.
 *
 * Uses the Authorisation Code flow with PKCE — the only supported
 * browser flow for modern Entra apps.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: environment.msal.clientId,
    authority: `https://login.microsoftonline.com/${environment.msal.tenantId}`,
    redirectUri: environment.msal.redirectUri,
    postLogoutRedirectUri: environment.msal.postLogoutRedirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // `sessionStorage` is preferred for SPAs; `localStorage` survives
    // tab restarts but is slightly more exposed to XSS. LabFlow is an
    // internal tool behind SSO — session is enough.
    cacheLocation: 'sessionStorage',
    // CRITICAL: must be true for redirect flow in normal (non-incognito) browsers.
    // During the login redirect round-trip MSAL writes a PKCE `code_verifier` and
    // `nonce` to sessionStorage BEFORE navigating away. On return, some browsers
    // (Chromium with Enhanced Tracking Protection, Edge, Firefox with ITP, or any
    // browser with an extension that intercepts cross-origin navigations) may read
    // from a different sessionStorage context, causing MSAL to fail with
    // "state_mismatch" or silently loop. Setting this to `true` makes MSAL
    // duplicate the critical auth-state fields into a same-site cookie as a
    // fallback, which survives the redirect reliably in all browser environments.
    // This is the exact scenario Microsoft documents at:
    // https://learn.microsoft.com/en-us/entra/msal/browser/configuration#storeautstatecincookie
    storeAuthStateInCookie: true,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        // Verbose logging in dev to diagnose auth issues; Warning-only in prod.
        if (import.meta.env.DEV) {
          console.log(`[msal][${LogLevel[level]}]`, message);
        } else if (level === LogLevel.Error) {
          console.error('[msal]', message);
        }
      },
      piiLoggingEnabled: false,
      logLevel: import.meta.env.DEV ? LogLevel.Verbose : LogLevel.Warning,
    },
  },
};

/** Scopes requested when acquiring an access token for the LabFlow API. */
export const labFlowApiRequest: { readonly scopes: readonly string[] } = {
  scopes: [environment.msal.apiScope],
};

/** Scopes requested when acquiring an access token for the Gateways API. */
export const gatewaysApiRequest: { readonly scopes: readonly string[] } = {
  scopes: [import.meta.env.VITE_GATEWAYS_API_SCOPE as string],
};

/** Login scopes: OpenID + profile are standard for signing the user in. */
export const loginRequest: { readonly scopes: readonly string[] } = {
  scopes: ['openid', 'profile', 'offline_access', environment.msal.apiScope],
};
